import { useState, type FormEvent } from 'react'
import { ArrowRight } from 'lucide-react'
import { PRIVACY_POLICY_URL } from '@/lib/config'

interface EmailCaptureProps {
  onSubmit: (email: string) => Promise<void> | void
  defaultEmail?: string
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

export function EmailCapture({ onSubmit, defaultEmail }: EmailCaptureProps) {
  const [email,    setEmail]    = useState(defaultEmail ?? '')
  const [status,   setStatus]   = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!email) return
    setStatus('submitting')
    setErrorMsg(null)
    try {
      await onSubmit(email.trim())
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong - try again?')
    }
  }

  if (status === 'success') {
    return (
      <div className="fq-email-success" role="status">
        Sent. Check your inbox for your match summary.
      </div>
    )
  }

  return (
    <form className="fq-email-capture" onSubmit={handleSubmit} noValidate>
      <div className="fq-email-field">
        <input
          id="fq-email"
          className="fq-email-input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={status === 'submitting'}
        />
        <button
          type="submit"
          className="fq-email-submit"
          disabled={status === 'submitting' || !email}
          aria-label="Send"
        >
          <ArrowRight size={16} strokeWidth={2.5} />
        </button>
      </div>
      {status === 'error' && errorMsg && (
        <p className="fq-email-error" role="alert">{errorMsg}</p>
      )}
      <p className="fq-email-privacy">
        We handle your email per our{' '}
        <a href={PRIVACY_POLICY_URL} target="_top" rel="noopener">privacy policy</a>.
        Unsubscribe anytime.
      </p>
    </form>
  )
}
